/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { EntitySegmentGroupList } from '../../Entities/entities';
import { getSegmentationService } from '../../Utilities/GetSegmentationService/GetSegmentationService';

/**
 * React hook to get the list of segment groups available for an organisation.
 * @param {string} cacheKey Optional cache key that will allow expiring of API data.
 * @export
 * @returns {EntitySegmentGroupList | null} Segment group list, or null if no value is available.
 */
export function useSegmentGroups(cacheKey = `1`): EntitySegmentGroupList | null {
  // Use of cacheKey as a dependency enables useMemo and useObservable to update if the value changes
  // To ensure updates, typically set this value to `new Date().toISOString()`
  const segmentationService = getSegmentationService();

  const groups$ = useMemo((): Observable<EntitySegmentGroupList | null> => {
    return segmentationService.getGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, segmentationService]);

  const [groups] = useObservable(groups$, [cacheKey, segmentationService]);

  return groups as EntitySegmentGroupList;
}
